/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormGroup,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	CircularProgress,
} from '@mui/material';
import {
	ITransaction,
	CreditCardInquiryTypeEnum,
	ICreditCardInquiryRequest,
	OperationalRequestCategoryEnum,
	ICreditCardInquiryTransactionInfo,
} from 'features/salesOrder/types';
import { FileUploader } from './FileUploader';
import { createOperationalRequest } from 'features/salesOrder/services';
import { extractErrorMessage } from 'features/quotation/helpers';
import { ShowAlertState } from 'features/common/types';
import { formatDateTimeToShowUser } from 'features/common/helpers';
import { fileToBase64Attachment } from '../../helpers';
import { CREDIT_CARD_INQUIRY_TYPE_LABELS } from '../../../../../features/salesOrder/constants';

interface CreditCardInquiryModalProps {
	open: boolean;
	onClose: () => void;
	transactions: ITransaction[];
	saleOrderId: string;
	onSuccess: (caseId: number) => void;
	onSetAlert: (alert: ShowAlertState) => void;
}

const inquiryTypes: {
	value: CreditCardInquiryTypeEnum;
	label: string;
}[] = (Object.entries(CREDIT_CARD_INQUIRY_TYPE_LABELS) as Array<[CreditCardInquiryTypeEnum, string]>).map(
	([value, label]) => ({
		value,
		label,
	}),
);

const isSameTransaction = (a: ITransaction, b: ITransaction) =>
	a.transactionId === b.transactionId &&
	a.authorizationId === b.authorizationId &&
	a.platformType === b.platformType &&
	a.relationType === b.relationType;

const transactionKey = (t: ITransaction) =>
	`${t.transactionId}::${t.authorizationId ?? 'no-auth'}::${t.platformType}::${t.relationType}`;

export const CreditCardInquiryModal: React.FC<CreditCardInquiryModalProps> = ({
	open,
	onClose,
	transactions,
	saleOrderId,
	onSuccess,
	onSetAlert,
}) => {
	const [inquiryType, setInquiryType] = useState<CreditCardInquiryTypeEnum>(CreditCardInquiryTypeEnum.Chargeback);
	const [selectedTransactions, setSelectedTransactions] = useState<ITransaction[]>([]);
	const [description, setDescription] = useState('');
	const [attachments, setAttachments] = useState<File[]>([]);
	const [loading, setLoading] = useState(false);

	const handleInquiryTypeChange = (event: SelectChangeEvent) => {
		setInquiryType(event.target.value as CreditCardInquiryTypeEnum);
	};

	const handleTransactionToggle = (transaction: ITransaction) => {
		setSelectedTransactions((prev) => {
			const exists = prev.some((t) => isSameTransaction(t, transaction));

			if (exists) {
				return prev.filter((t) => !isSameTransaction(t, transaction));
			}

			return [...prev, transaction];
		});
	};

	const handleSelectAllTransactions = () => {
		if (selectedTransactions.length === transactions.length) {
			setSelectedTransactions([]);
		} else {
			setSelectedTransactions(transactions);
		}
	};

	const handleSubmit = async () => {
		if (selectedTransactions.length === 0) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: 'Debe seleccionar al menos una transacción',
			});
			return;
		}

		if (!description.trim()) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: 'Debe ingresar una descripción del problema',
			});
			return;
		}

		try {
			setLoading(true);

			const files = Array.from(attachments.length > 0 ? attachments : []);

			const filesToAttachments = await Promise.all(files.map(fileToBase64Attachment));
			const transactionsInfo: ICreditCardInquiryTransactionInfo[] = selectedTransactions.map((t) => ({
				transactionId: t.transactionId,
				authorizationId: t.authorizationId,
				relationType: t.relationType,
				platformType: t.platformType,
			}));

			const requestData: ICreditCardInquiryRequest = {
				requestCategory: OperationalRequestCategoryEnum.CreditCardInquiry,
				requestType: inquiryType,
				saleOrderId,
				serviceIds: [], // No aplica para consultas de tarjetas
				transactionsInfo: transactionsInfo,
				description,
				attachments: filesToAttachments.length > 0 ? filesToAttachments : undefined,
			};

			// // Construir payload legible para testing
			// const inquiryTypeLabel = inquiryTypes.find((t) => t.value === inquiryType)?.label || inquiryType;
			// const payloadForAlert = {
			// 	categoria: 'Consulta sobre Cobros con Tarjeta',
			// 	tipoConsulta: inquiryTypeLabel,
			// 	ordenVenta: saleOrderId,
			// 	dealId,
			// 	esDealBitrix: isBitrixDeal,
			// 	transaccionesSeleccionadas: selectedTransactions.map((transactionId) => {
			// 		const transaction = transactions.find((t) => t.transactionId === transactionId);
			// 		return transaction
			// 			? {
			// 					transactionId: transaction.transactionId,
			// 					fecha: formatDateTimeToShowUser(transaction.createdAt),
			// 					monto: `${transaction.transactionCurrency} ${transaction.transactionAmount}`,
			// 					montoMonedaPrincipal: transaction.amountInMainCurrency,
			// 					tipoRelacion: transaction.relationType,
			// 					plataforma: transaction.platformType,
			// 					observaciones: transaction.observations || 'Sin observaciones',
			// 			  }
			// 			: { transactionId, error: 'Transacción no encontrada' };
			// 	}),
			// 	descripcion: description,
			// 	archivosAdjuntos: attachments.map((f) => ({ nombre: f.name, tamaño: `${(f.size / 1024).toFixed(2)} KB` })),
			// };
			//
			// // Alert para testing - mostrar payload completo
			// console.log('PAYLOAD DE CONSULTA SOBRE COBROS:', payloadForAlert);
			// window.alert(`PAYLOAD DE CONSULTA SOBRE COBROS:\n\n${JSON.stringify(payloadForAlert, null, 2)}`);

			const response = await createOperationalRequest(requestData);
			onSuccess(response.data.caseId);
			onSetAlert({
				show: true,
				severity: 'success',
				message: `Consulta creada exitosamente. Caso #${response.data.caseId} creado en el CRM.`,
			});

			// Reset form
			setSelectedTransactions([]);
			setDescription('');
			setAttachments([]);
			onClose();
		} catch (error) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al crear la consulta'),
			});
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		if (!loading) {
			setSelectedTransactions([]);
			setDescription('');
			setAttachments([]);
			onClose();
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle>Consulta sobre cobros con tarjeta de crédito</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
					<FormControl fullWidth>
						<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
							Tipo de consulta *
						</Typography>
						<Select value={inquiryType} onChange={handleInquiryTypeChange} disabled={loading}>
							{inquiryTypes.map((type) => (
								<MenuItem key={type.value} value={type.value}>
									{type.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<Box>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
							<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
								Seleccionar transacciones relacionadas *
							</Typography>
							{transactions.length > 0 && (
								<FormControlLabel
									control={
										<Checkbox
											checked={selectedTransactions.length === transactions.length && transactions.length > 0}
											indeterminate={
												selectedTransactions.length > 0 && selectedTransactions.length < transactions.length
											}
											onChange={handleSelectAllTransactions}
											disabled={loading}
										/>
									}
									label="Seleccionar todas"
								/>
							)}
						</Box>

						{transactions.length === 0 ? (
							<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
								No hay transacciones disponibles en esta orden.
							</Typography>
						) : (
							<FormGroup>
								{transactions.map((transaction) => {
									const isSelected = selectedTransactions.some((t) => isSameTransaction(t, transaction));
									return (
										<Box
											key={transactionKey(transaction)}
											sx={{
												p: 2,
												mb: 1,
												border: '1px solid',
												borderColor: isSelected ? 'primary.main' : 'divider',
												borderRadius: 1,
												backgroundColor: isSelected ? 'action.selected' : 'transparent',
											}}
										>
											<FormControlLabel
												control={
													<Checkbox
														checked={isSelected}
														onChange={() => handleTransactionToggle(transaction)}
														disabled={loading}
													/>
												}
												label={
													<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
														<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
															Transacción #{transaction.transactionId}
														</Typography>
														<Typography variant="caption" color="text.secondary">
															Fecha: {formatDateTimeToShowUser(transaction.createdAt)}
														</Typography>
														<Typography variant="caption" color="text.secondary">
															Monto: {transaction.transactionCurrency} {transaction.transactionAmount} |{' '}
															{transaction.amountInMainCurrency} (moneda principal)
														</Typography>
														<Typography variant="caption" color="text.secondary">
															Tipo de relación: {transaction.relationType} | Plataforma: {transaction.platformType}
														</Typography>
														{transaction.observations && (
															<Typography variant="caption" color="text.secondary">
																Observaciones: {transaction.observations}
															</Typography>
														)}
													</Box>
												}
											/>
										</Box>
									);
								})}
							</FormGroup>
						)}
					</Box>

					<TextField
						label="Descripción del problema *"
						multiline
						rows={4}
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						disabled={loading}
						fullWidth
						required
						helperText="Describa el problema identificado por el cliente. Si es un cobro duplicado, incluya detalles sobre los montos. Si es un desconocimiento, incluya la información proporcionada por el cliente."
					/>

					<FileUploader
						files={attachments}
						onChange={setAttachments}
						accept=".pdf,.jpg,.jpeg,.png"
						maxFiles={10}
						maxSizeMB={10}
						disabled={loading}
						label="Documentos de soporte (estado de cuenta, screenshots, etc.)"
					/>

					<Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
						<Typography variant="body2">
							<strong>Nota:</strong> El análisis del caso será mucho más rápido y eficiente si se logra recolectar la
							mayor cantidad de información posible. Generalmente se necesita el estado de cuenta del cliente cuando el
							cobro está duplicado, o un print de pantalla de los movimientos.
						</Typography>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancelar
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					disabled={loading}
					startIcon={loading && <CircularProgress size={16} />}
				>
					Crear consulta
				</Button>
			</DialogActions>
		</Dialog>
	);
};
